import { useContext, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext, DragEndEvent, DragStartEvent, DragOverlay, useDroppable,
  PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Input, Segmented, Select, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTasks, useUpdateTask, useTransitionTaskStatus } from "../../queries/tasks";
import { useProject } from "../../queries/projects";
import { useStaffDirectory, StaffMember } from "../../queries/users";
import { useSprints } from "../../queries/sprints";
import { computeDropResult } from "./dropLogic";
import { TaskProps, TaskStatus, SprintProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import AuthContext from "../../context/AuthContext";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { ProjectNav } from "../../components/ui";
import { PageShell } from "../../components/ui/PageShell";
import { TaskPriorityTag, SeverityChip } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import styles from "./board.module.css";

/** Column accent doubles as the workflow's temperature reading. */
const BOARD_COLUMNS: { status: TaskStatus; title: string; color: string }[] = [
  { status: "ready", title: "Ready", color: "var(--osi-brand-500)" },
  { status: "in_progress", title: "In Progress", color: "var(--osi-brand-700)" },
  { status: "in_review", title: "In Review", color: "var(--osi-warn)" },
  { status: "done", title: "Done", color: "var(--osi-ok)" },
];

const initials = (name?: string) =>
  (name || "").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function CardFace({
  task, assignee, dragging, overlay, onOpen,
}: { task: TaskProps; assignee?: StaffMember; dragging?: boolean; overlay?: boolean; onOpen?: () => void }) {
  const overdue = task.dueDate && task.status !== "done" && dayjs(task.dueDate).isBefore(dayjs(), "day");
  const cls = [
    styles.card,
    task.isBlocked ? styles.cardBlocked : "",
    dragging ? styles.cardDragging : "",
    overlay ? styles.cardOverlay : "",
  ].join(" ");

  return (
    <div className={cls}>
      <div className={styles.cardTop}>
        <span className={styles.key}>{task._id.slice(-4).toUpperCase()}</span>
        {task.isBlocked && <TaskPriorityTag priority="urgent" />}
        {onOpen && (
          <button
            type="button"
            className={styles.open}
            aria-label={`Open ${task.title}`}
            /* stopping pointerdown keeps dnd-kit's drag activation from firing
               when the user only wants to open the task — unchanged behaviour */
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
          >
            Open ↗
          </button>
        )}
      </div>
      <div className={styles.cardTitle}>{task.title}</div>
      {task.labels?.length > 0 && (
        <div className={styles.labels}>
          {task.labels.slice(0, 3).map((l) => <span className={styles.label} key={l}>{l}</span>)}
        </div>
      )}
      <div className={styles.cardFoot}>
        <SeverityChip severity={task.severity} />
        <TaskPriorityTag priority={task.priority} />
        {task.dueDate && (
          <span className={`${styles.due} ${overdue ? styles.dueOver : ""}`}>
            {dayjs(task.dueDate).format("D MMM")}
          </span>
        )}
        <span className={styles.footSpacer} />
        {task.storyPoints != null && <span className={styles.sp}>{task.storyPoints}</span>}
        <span
          className={`${styles.assignee} ${assignee ? "" : styles.assigneeNone}`}
          title={assignee?.name || "Unassigned"}
        >
          {assignee ? initials(assignee.name) : "—"}
        </span>
      </div>
    </div>
  );
}

function TaskCard({ task, assignee }: { task: TaskProps; assignee?: StaffMember }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id: task._id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Translate.toString(transform) }}
    >
      <CardFace task={task} assignee={assignee} dragging={isDragging} onOpen={() => navigate(`/tasks/${task._id}`)} />
    </div>
  );
}

function Column({
  status, title, color, tasks, users,
}: { status: TaskStatus; title: string; color: string; tasks: TaskProps[]; users: StaffMember[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const points = tasks.reduce((n, t) => n + (t.storyPoints || 0), 0);

  return (
    <div ref={setNodeRef} className={`${styles.column} ${isOver ? styles.columnOver : ""}`}>
      <div className={styles.columnHead} style={{ color }}>
        <span className={styles.columnTitle}>{title}</span>
        {points > 0 && <span className={styles.points}>{points} pts</span>}
        <span className={styles.columnCount}>{tasks.length}</span>
      </div>
      <div className={styles.columnBody}>
        <SortableContext items={tasks.map((t) => t._id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard key={task._id} task={task} assignee={users.find((u) => u._id === task.assigneeId)} />
          ))}
        </SortableContext>
        {tasks.length === 0 && <div className={styles.emptyCol}>Drop a task here</div>}
      </div>
    </div>
  );
}

export const BoardPage = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isStaff = userData?.role === "manager" || userData?.role === "agent";
  const { data: tasks, isLoading, isError, error } = useTasks({ projectId: projectId! });
  const { data: project } = useProject(projectId!);
  const { data: users } = useStaffDirectory({ enabled: isStaff });
  const { data: sprints } = useSprints();
  const updateTask = useUpdateTask(projectId!);
  const transitionStatus = useTransitionTaskStatus(projectId!);

  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<"all" | "mine">("all");
  const [sprintFilter, setSprintFilter] = useState<string | undefined>();
  const [activeId, setActiveId] = useState<string | null>(null);

  // 4px activation distance: lets a click land on the card without starting a drag
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const allTasks: TaskProps[] = useMemo(() => tasks || [], [tasks]);
  const userList: StaffMember[] = users || [];

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTasks
      .filter((t) => (q ? t.title.toLowerCase().includes(q) : true))
      .filter((t) => (scope === "mine" ? t.assigneeId === userData?._id : true))
      .filter((t) => (sprintFilter ? t.sprintId === sprintFilter : true));
  }, [allTasks, query, scope, userData, sprintFilter]);

  // NOTE: columns are built from the unfiltered set so drop ranks stay correct.
  const columns = BOARD_COLUMNS.reduce<Record<string, TaskProps[]>>((acc, { status }) => {
    acc[status] = allTasks.filter((t) => t.status === status).sort((a, b) => a.rank - b.rank);
    return acc;
  }, {});
  const shown = BOARD_COLUMNS.reduce<Record<string, TaskProps[]>>((acc, { status }) => {
    acc[status] = columns[status].filter((t) => visible.includes(t));
    return acc;
  }, {});

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeTask = allTasks.find((t) => t._id === active.id);
    if (!activeTask) return;

    const overIsColumn = BOARD_COLUMNS.some((c) => c.status === over.id);
    const overColumnStatus = overIsColumn
      ? (over.id as TaskStatus)
      : allTasks.find((t) => t._id === over.id)?.status;
    if (!overColumnStatus) return;

    const overTaskId = overIsColumn ? null : (over.id as string);
    const { status, rank } = computeDropResult(columns, String(active.id), overColumnStatus, overTaskId);

    try {
      if (status !== activeTask.status) {
        await transitionStatus.mutateAsync({ id: activeTask._id, status });
      }
      await updateTask.mutateAsync({ id: activeTask._id, data: { rank } });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Couldn't move that task there");
    }
  };

  const activeTask = allTasks.find((t) => t._id === activeId);
  const wip = columns["in_progress"]?.length || 0;
  const blocked = allTasks.filter((t) => t.isBlocked).length;

  return (
    <PageShell
      kind="board"
      crumbs={[{ label: "Projects", to: "/projects" }, { label: project?.name || "Project" }]}
      title={project?.name || "Board"}
      meta={[
        { label: "In flight", value: <span className="osi-num">{wip}</span> },
        { label: "Blocked", value: <span className="osi-num" style={{ color: blocked ? "var(--osi-danger)" : undefined }}>{blocked}</span> },
        { label: "Total", value: <span className="osi-num">{allTasks.length}</span> },
      ]}
      actions={
        <>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "var(--osi-text-faint)" }} />}
            placeholder="Filter tasks"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: 200 }}
          />
          <Segmented
            value={scope}
            onChange={(v) => setScope(v as "all" | "mine")}
            options={[{ label: "All", value: "all" }, { label: "Mine", value: "mine" }]}
          />
          <Select
            allowClear
            style={{ width: 170 }}
            placeholder="Any sprint"
            value={sprintFilter}
            onChange={setSprintFilter}
            options={(sprints || []).map((sp: SprintProps) => ({
              label: sp.status === "active" ? `${sp.name} (active)` : sp.name,
              value: sp._id,
            }))}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/projects/${projectId}/backlog`)}>New task</Button>
        </>
      }
      tabs={<ProjectNav projectId={projectId!} active="board" />}
    >
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        {allTasks.length === 0 ? (
          <div style={{ padding: 24, maxWidth: 640 }}>
            <EmptyState
              title="Nothing on the board yet"
              description="Tasks promoted from the backlog land in Ready. Move the first one across to start the sprint."
              actions={<Button type="primary" onClick={() => navigate(`/projects/${projectId}/backlog`)}>Open backlog</Button>}
            />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
          >
            <div className={styles.board}>
              {BOARD_COLUMNS.map(({ status, title, color }) => (
                <Column
                  key={status}
                  status={status}
                  title={title}
                  color={color}
                  tasks={shown[status]}
                  users={userList}
                />
              ))}
            </div>
            <DragOverlay dropAnimation={null}>
              {activeTask && (
                <CardFace
                  task={activeTask}
                  assignee={userList.find((u) => u._id === activeTask.assigneeId)}
                  overlay
                />
              )}
            </DragOverlay>
          </DndContext>
        )}
      </DisplayComponent>
    </PageShell>
  );
};
